import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Input, Button, Spinner } from "reactstrap";
import Reason from "common/utilities/Reason/Reason";
import { showToastMessage } from "common/service/CommonService";
import styles from "../css/roleRightsMatrix.module.scss";
import sf from "../../../../assets/scss/common/searchFilter.module.scss"
import {
  useRightsService,
  RightsMatrixModule,
  AssignedRight,
} from "modules/Library/Role/api/useRightsQuery";
import { categories } from "../helper/roleconstant";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { RoleRightsRef, StudyRole } from "../helper/role.types";
import { usePermission } from "context/PermissionContext";
import { LIBRARY_PATHS } from "Routes/paths";

interface Props {
  role?: StudyRole;
  onClose?: () => void;
}

const RoleRightsMatrix = forwardRef<RoleRightsRef, Props>(
  ({ role, onClose }, ref) => {
    document.title = "Role Rights | ZENTIXS VISTA";
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const roleIdNum = id ? Number(id) : undefined;

    // Retrieve role from props or router state
    const roleData = role || (location.state?.role as StudyRole | undefined);

    const [modules, setModules] = useState<RightsMatrixModule[]>([]);
    const [permissions, setPermissions] = useState<AssignedRight[]>([]);
    const [reasonOpen, setReasonOpen] = useState(false);
    const [searchModule, setSearchModule] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [activeTab, setActiveTab] = useState("GENERAL SETTINGS");
    const [activeSubModuleId, setActiveSubModuleId] = useState<number | null>(null);

    const { isModuleVisible } = usePermission();

    const groupedModules = useMemo(() => {
      const result: Record<string, RightsMatrixModule[]> = {};
      categories.forEach((cat: any) => result[cat.id] = []);

      modules.forEach((m: RightsMatrixModule) => {
        const name = m.formname.toUpperCase();
        let bestCategory = null;
        let longestMatchLength = 0;

        categories.forEach((cat: any) => {
          cat.match.forEach((keyword: string) => {
            const normalizedKeyword = keyword.replace(/_/g, ' ').toUpperCase();
            const normalizedName = name.replace(/_/g, ' ');
            const re = new RegExp(`\\b${normalizedKeyword}\\b`, 'i');
            if (re.test(normalizedName)) {
              if (normalizedKeyword.length > longestMatchLength) {
                longestMatchLength = normalizedKeyword.length;
                bestCategory = cat;
              }
            }
          });
        });

        const category = bestCategory || categories.find((cat: any) => cat.id === 'OTHERS');
        if (category) {
          result[category.id].push(m);
        }
      });
      return result;
    }, [modules, categories]);

    useEffect(() => {
      if (groupedModules[activeTab]?.length > 0) {
        if (!activeSubModuleId || !groupedModules[activeTab].some((m: RightsMatrixModule) => m.formid === activeSubModuleId)) {
          setActiveSubModuleId(groupedModules[activeTab][0].formid);
        }
      } else {
        setActiveSubModuleId(null);
      }
    }, [activeTab, groupedModules]);

    useEffect(() => {
      const enabledCats = categories.filter((cat: any) => groupedModules[cat.id]?.length > 0);
      if (enabledCats.length > 0 && !enabledCats.some((cat: any) => cat.id === activeTab)) {
        setActiveTab(enabledCats[0].id);
      }
    }, [groupedModules, activeTab]);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedSearch(searchModule);
      }, 500);

      return () => clearTimeout(handler);
    }, [searchModule]);

    /* -----------------------------------------------------
       Correct Hook Call
    ----------------------------------------------------- */
    const {
      SelectAllQuery,
      SelectAssignedQuery,
      InsertRightsMutation,
    } = useRightsService({
      roleId: roleIdNum,
      searchText: debouncedSearch,
    });

    /* -----------------------------------------------------
       RIGHTS MATRIX (all modules)
    ----------------------------------------------------- */
    const {
      data: matrixResp,
      isLoading: isMatrixLoading,
      isError: isMatrixError,
      error: matrixError,
      isFetching: isMatrixFetching,
    } = SelectAllQuery;

    /* -----------------------------------------------------
       ASSIGNED RIGHTS (for selected role)
    ----------------------------------------------------- */
    const {
      data: assignedResp,
      isLoading: isAssignedLoading,
      isError: isAssignedError,
      error: assignedError,
      refetch: refetchAssigned,
      isFetching: isAssignedFetching,
    } = SelectAssignedQuery;
    const [searchFocused, setSearchFocused] = useState(false);
    /* -----------------------------------------------------
       Normalize MATRIX
    ----------------------------------------------------- */
    useEffect(() => {
      const raw = matrixResp?.data?.data ?? matrixResp?.data ?? matrixResp;
      if (Array.isArray(raw)) {
        const filtered = (raw as RightsMatrixModule[]).filter(
          (m) => m && isModuleVisible(m.formname)
        );
        setModules(filtered);
        // setModules(raw as RightsMatrixModule[]);
      }
    }, [matrixResp]);

    /* -----------------------------------------------------
       Normalize ASSIGNED RIGHTS
    ----------------------------------------------------- */
    useEffect(() => {
      const raw = assignedResp?.data?.data ?? assignedResp?.data ?? assignedResp;
      if (Array.isArray(raw)) {
        setPermissions(raw as AssignedRight[]);
      }
    }, [assignedResp]);

    /* -----------------------------------------------------
       Expose save() to parent component
    ----------------------------------------------------- */
    useImperativeHandle(ref, () => ({
      save: () => setReasonOpen(true),
    }));

    /* -----------------------------------------------------
       Helpers
    ----------------------------------------------------- */
    const hasPermission = (formid: number, actionid: number) =>
      permissions.some((p) => p.formid === formid && p.actionid === actionid);

    const togglePermission = (formid: number, actionid: number) => {
      const targetModule = modules.find((m) => m.formid === formid);
      const formnameUpper = targetModule?.formname?.toUpperCase() || "";
      const isGlobalStudyModule = formnameUpper.includes("GLOBAL STUDY") || formnameUpper.includes("GLOBAL STUDIES");

      setPermissions((prev) => {
        const exists = prev.some(
          (p) => p.formid === formid && p.actionid === actionid,
        );

        if (exists) {
          return prev.filter(
            (p) => !(p.formid === formid && p.actionid === actionid),
          );
        }

        if (isGlobalStudyModule) {
          // Mutually exclusive: filter out any other actions for this formid, then add the new one
          return [
            ...prev.filter((p) => p.formid !== formid),
            { formid, actionid },
          ];
        }

        return [...prev, { formid, actionid }];
      });
    };

    const moduleAllSelected = (m: RightsMatrixModule) =>
      m?.actions?.every((a) => hasPermission(m.formid, a.actionid)) ?? false;

    const toggleSelectAll = (m: RightsMatrixModule) => {
      const allSelected = moduleAllSelected(m);
      const moduleKeys = (m?.actions || []).map((a) => ({
        formid: m.formid,
        actionid: a.actionid,
      }));

      setPermissions((prev) =>
        allSelected
          ? prev.filter(
            (p) =>
              !moduleKeys.some(
                (mk) => mk.formid === p.formid && mk.actionid === p.actionid,
              ),
          )
          : [
            ...prev,
            ...moduleKeys.filter(
              (mk) =>
                !prev.some(
                  (p) => p.formid === mk.formid && p.actionid === mk.actionid,
                ),
            ),
          ],
      );
    };

    /* -----------------------------------------------------
       SAVE RIGHTS
    ----------------------------------------------------- */

    const handleClose = () => {
      if (onClose) {
        onClose();
      } else {
        navigate(LIBRARY_PATHS.ROLE_LIST);
      }
    };

    const handleReasonSubmit = (payload: {
      id: number | null;
      reason: string;
    }) => {
      // debugger;
      if (!Number.isFinite(roleIdNum)) {
        showToastMessage("error", "Invalid role ID");
        return;
      }

      const apiPayload = {
        roleid: roleIdNum,
        rights: permissions,
        updatereason: payload.reason,
      };

      InsertRightsMutation.mutate(apiPayload, {
        onSuccess: (res) => {
          showToastMessage(
            "success",
            res?.message ?? "Rights saved successfully!",
          );
          setReasonOpen(false);
          handleClose();
          SelectAssignedQuery.refetch?.();
        },
        onError: (err) => {
          showToastMessage(
            "error",
            err?.message ?? "Error while saving rights",
          );
        },
      });
    };

    /* -----------------------------------------------------
       RENDER
    ----------------------------------------------------- */
    const isLoading = isMatrixLoading || isAssignedLoading || isMatrixFetching || isAssignedFetching;

    const displayModules = modules || [];

    return (
      <div className="py-4 px-2">
        <div className={styles.headerWrapper}>
          <div>
            <h6 className={styles.headerTitle}>
              <i className={`ri-shield-keyhole-line text-primary ${styles.headerIcon}`}></i>
              {roleData?.title && (
                <span className={styles.roleBadge}>{roleData.title}</span>
              )}
            </h6>
            <p className={styles.headerSubtext}>
              Configure access permissions for each module
            </p>
          </div>
          <div className="col-sm-auto">
          </div>
        </div>

        <div className={styles.nestedContainer}>
          <div className={styles.topTabs}>
            {categories
              .filter((cat: any) => groupedModules[cat.id]?.length > 0)
              .map((cat: any) => (
                <div
                  key={cat.id}
                  className={`${styles.tabItem} ${activeTab === cat.id ? styles.activeTab : ""}`}
                  onClick={() => setActiveTab(cat.id)}
                >
                  <i className={cat.icon}></i> {cat.label}
                  {groupedModules[cat.id]?.length > 0 && (
                    <span className="badge rounded-pill bg-primary-subtle text-primary ms-1" style={{ fontSize: '9px' }}>
                      {groupedModules[cat.id].length}
                    </span>
                  )}
                </div>
              ))}
          </div>

          <div className={styles.actionBar}>
            <div className={styles.searchSection}>
              <div className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}>
                <i className={`ri-search-line ${sf.searchIcon}`} />
                <input
                  type="text"
                  placeholder="Search Modules..."
                  value={searchModule ?? ""}
                  className={sf.searchInput}
                  onChange={(e) => setSearchModule(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                {searchModule && (
                  <i
                    className={`ri-close-line ${sf.clearIcon}`}
                    onClick={() => setSearchModule("")}
                  />
                )}
              </div>
            </div>

            {activeSubModuleId && !isLoading && (() => {
              const name = groupedModules[activeTab]?.find((m: RightsMatrixModule) => m.formid === activeSubModuleId)?.formname?.toUpperCase() || "";
              const isGlobalStudy = name.includes("GLOBAL STUDY") || name.includes("GLOBAL STUDIES");
              if (isGlobalStudy) return null;

              return (
                <div className="d-flex align-items-center gap-3">
                  <span className="small text-muted fw-medium">All Permissions</span>
                  <Input
                    type="switch"
                    style={{ cursor: 'pointer' }}
                    checked={groupedModules[activeTab]?.find((m: RightsMatrixModule) => m.formid === activeSubModuleId) ? moduleAllSelected(groupedModules[activeTab].find((m: RightsMatrixModule) => m.formid === activeSubModuleId)!) : false}
                    onChange={() => {
                      const mod = groupedModules[activeTab].find((m: RightsMatrixModule) => m.formid === activeSubModuleId);
                      if (mod) toggleSelectAll(mod);
                    }}
                  />
                </div>
              );
            })()}
            {isLoading && activeSubModuleId && (
              <div className={`${styles.skeletonBase}`} style={{ height: '24px', width: '120px', borderRadius: '4px' }} />
            )}
          </div>

          <div className={styles.mainLayout}>
            {isLoading ? (
              <>
                <div className={styles.leftSidebar}>
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className={`${styles.skeletonBase} ${styles.skeletonSidebarItem}`}
                      style={{ opacity: 1 - (i * 0.1) }}
                    />
                  ))}
                </div>
                <div className={styles.rightContent}>
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`${styles.skeletonBase} ${styles.skeletonTableRow}`}
                      style={{ opacity: 1 - (i * 0.15) }}
                    />
                  ))}
                </div>
              </>
            ) : displayModules.length === 0 ? (
              <div className="w-100 d-flex flex-column align-items-center justify-content-center p-5 text-muted">
                <i className="ri-search-2-line fs-32 mb-2 opacity-25"></i>
                <span className="fw-medium">No items in this category</span>
                <p className="small opacity-75">Try adjusting your search or switching tabs</p>
              </div>
            ) : (
              <>
                {/* Inner Sidebar for Sub-modules */}
                <div className={styles.leftSidebar}>
                  {groupedModules[activeTab]?.length > 0 ? (
                    groupedModules[activeTab].map((m: RightsMatrixModule) => (
                      <div
                        key={m.formid}
                        className={`${styles.sidebarItem} ${activeSubModuleId === m.formid ? styles.activeSidebar : ""}`}
                        onClick={() => setActiveSubModuleId(m.formid)}
                      >
                        {m.formname}
                        <i className="ri-arrow-right-s-line opacity-50"></i>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-4 mb-4 text-muted small">No items in this category</div>
                  )}
                </div>

                {/* Right Content Table */}
                <div className={styles.rightContent}>
                  {activeSubModuleId ? (
                    <div className={styles.rightsTableContainer}>
                      <table className={styles.rightsTable}>
                        <thead>
                          <tr>
                            <th style={{ width: '60%' }}>Action Name</th>
                            <th className="text-center">Access Control</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedModules[activeTab]?.find((m: RightsMatrixModule) => m.formid === activeSubModuleId)?.actions.map((a: any) => {
                            const isSelected = hasPermission(activeSubModuleId, a.actionid);
                            return (
                              <tr key={`${activeSubModuleId}_${a.actionid}`}>
                                <td>
                                  <div className="fw-semibold text-dark">{a.actionname}</div>
                                  <div className="text-muted small">Allows user to {a.actionname.toLowerCase()} items in this module.</div>
                                </td>
                                <td>
                                  <div className="d-flex justify-content-center">
                                    <div className={styles.actionToggleWrapper}>
                                      <div className={`${styles.statusIndicator} ${isSelected ? styles.active : styles.inactive}`} />
                                      <Input
                                        type="switch"
                                        checked={isSelected}
                                        onChange={() => togglePermission(activeSubModuleId, a.actionid)}
                                        className="cursor-pointer"
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                      <i className="ri-layout-grid-line fs-24 mb-2"></i>
                      <span>Select a module from the sidebar to view rights</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>


        </div>
        {/* Footer Actions inside Role Rights Matrix */}
        <div className={`${styles.footerActions} ${styles.stickyFooter}`}>
          <Button
            color="secondary"
            outline
            onClick={handleClose}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={() => setReasonOpen(true)}
            outline
            disabled={InsertRightsMutation.isPending}
          >
            Save Rights
          </Button>
        </div>
        <Reason
          isOpen={reasonOpen}
          toggle={() => setReasonOpen(false)}
          action="Update Rights"
          id={roleData?.roleid ?? roleData?.id ?? null}
          click={handleReasonSubmit}
          isLoading={!!InsertRightsMutation.isPending}
        />
      </div>
    );
  },
);

export default RoleRightsMatrix;
